import React from 'react';
import { Box, Button } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

/** Overview and Edit on a source home. Evidence joins only when it has content. */
export type SourceHomePanel = 'triage' | 'work' | 'intel';

const ROWS: Array<{ panel: SourceHomePanel; label: string; testId: string }> = [
    { panel: 'triage', label: 'Overview', testId: 'WB_DRAWER_SOURCE_HOME_OVERVIEW' },
    { panel: 'work', label: 'Edit', testId: 'WB_DRAWER_SOURCE_HOME_EDIT' },
    { panel: 'intel', label: 'Evidence', testId: 'WB_DRAWER_SOURCE_HOME_EVIDENCE' },
];

/**
 * Collapsed strip under the source-home verbs. Closed until tapped.
 * Opening one panel closes the other so the scroll stays short.
 */
const DrawerSourceHomeAccordion: React.FC<{
    openPanel: SourceHomePanel | null;
    showEvidence: boolean;
    onToggle: (panel: SourceHomePanel) => void;
    overview: React.ReactNode;
    edit: React.ReactNode;
    evidence: React.ReactNode;
}> = ({ openPanel, showEvidence, onToggle, overview, edit, evidence }) => {
    const panels: Record<SourceHomePanel, React.ReactNode> = {
        triage: overview,
        work: edit,
        intel: evidence,
    };
    const rows = ROWS.filter((row) => row.panel !== 'intel' || showEvidence);

    return (
        <Box
            data-testid="WB_DRAWER_SOURCE_HOME_ACCORDION"
            sx={{ borderTop: '1px solid var(--color-border)' }}
        >
            {rows.map((row) => {
                const open = openPanel === row.panel;
                const panelId = `wb-source-home-panel-${row.panel}`;
                return (
                    <Box key={row.panel} sx={{ borderBottom: '1px solid var(--color-border)' }}>
                        <Button
                            type="button"
                            fullWidth
                            data-testid={row.testId}
                            aria-expanded={open}
                            aria-controls={open ? panelId : undefined}
                            onClick={() => onToggle(row.panel)}
                            sx={{
                                justifyContent: 'space-between',
                                textTransform: 'none',
                                fontWeight: 650,
                                fontSize: '0.875rem',
                                color: open ? 'var(--color-accent-text)' : 'var(--color-text-secondary)',
                                borderRadius: 0,
                                px: 2,
                                py: 1.25,
                                minHeight: 44,
                                '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.06)' },
                                '&:focus-visible': {
                                    outline: '2px solid var(--color-accent)',
                                    outlineOffset: '-2px',
                                },
                            }}
                        >
                            {row.label}
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                style={{
                                    fontSize: '0.75rem',
                                    transform: open ? 'rotate(180deg)' : undefined,
                                }}
                            />
                        </Button>
                        {open && (
                            <Box id={panelId} sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
                                {panels[row.panel]}
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default DrawerSourceHomeAccordion;
